import type { BudgetCategory, BudgetSummary, MonthSpend } from '../../services/budgetService';
import type { Currency } from '../../config/currencies';

/** Colour token of each spend category (the "other" bucket is muted, not a series colour). */
const CATEGORY_COLOUR_VAR: Readonly<Record<BudgetCategory, string>> = {
  salt: '--color-chart-kh',
  food: '--color-chart-po4',
  filter_media: '--color-chart-no3',
  supplements: '--color-chart-ca',
  electricity: '--color-chart-mg',
  water: '--color-chart-series6',
  livestock: '--color-chart-series7',
  equipment: '--color-chart-series8',
  other: '--color-text-muted',
};

export function categoryColourVar(category: BudgetCategory): string {
  return CATEGORY_COLOUR_VAR[category];
}

/** Amounts are stored ×100 ("cents") in the user's currency (Settings → General). */
export function formatMoney(cents: number, language: string, currency: Currency, options: { compact?: boolean } = {}): string {
  return new Intl.NumberFormat(language, {
    style: 'currency',
    currency,
    ...(options.compact ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}),
  }).format(cents / 100);
}

/** Decimals the currency is written with (2 for EUR, 0 for JPY). */
export function currencyFractionDigits(currency: Currency): number {
  return new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
}

/** "€", "$", "CHF", "¥" … as the language writes it. */
export function currencySymbol(currency: Currency, language: string): string {
  const part = new Intl.NumberFormat(language, { style: 'currency', currency }).formatToParts(0).find((p) => p.type === 'currency');
  return part?.value ?? currency;
}

/**
 * An amount as typed into the form, e.g. "12,50", "12.5", "1.234,56" or "1,234.56", in cents;
 * null when it is not a positive amount or has more decimals than the currency (`fractionDigits`).
 */
export function parseAmountToCents(input: string, fractionDigits = 2): number | null {
  const raw = input.replace(/[\s€$£¥]/g, '');
  if (!/^\d[\d.,]*$/.test(raw)) return null;
  // Thousands groups: 1–3 digits, then groups of exactly 3.
  const grouped = (digits: string, group: string): string | null => {
    const parts = digits.split(group);
    if (parts.length > 1 && (!/^\d{1,3}$/.test(parts[0]!) || parts.slice(1).some((p) => !/^\d{3}$/.test(p)))) return null;
    return parts.join('');
  };
  let normalised: string | null;
  const lastDot = raw.lastIndexOf('.');
  const lastComma = raw.lastIndexOf(',');
  if (lastDot >= 0 && lastComma >= 0) {
    // Both present: the later one is the decimal separator, the other groups thousands.
    const decimal = lastDot > lastComma ? '.' : ',';
    const group = decimal === '.' ? ',' : '.';
    const at = raw.lastIndexOf(decimal);
    const whole = grouped(raw.slice(0, at), group);
    normalised = whole === null ? null : `${whole}.${raw.slice(at + 1)}`;
  } else {
    const sep = lastDot >= 0 ? '.' : lastComma >= 0 ? ',' : null;
    const parts = sep === null ? [raw] : raw.split(sep);
    // A single separator followed by 1–2 digits is the decimal point; otherwise it groups thousands.
    if (sep !== null && parts.length === 2 && parts[1]!.length <= 2) normalised = `${parts[0]}.${parts[1]}`;
    else normalised = sep === null ? raw : grouped(raw, sep);
  }
  if (normalised === null) return null;
  // Digits, optionally one "." and more digits, at most `fractionDigits` of them.
  const [whole, decimals, ...rest] = normalised.split('.');
  const digitsOnly = /^\d+$/;
  if (rest.length > 0 || !digitsOnly.test(whole ?? '') || (decimals !== undefined && !digitsOnly.test(decimals))) return null;
  if ((decimals?.length ?? 0) > fractionDigits) return null;
  const cents = Math.round(Number(normalised) * 100);
  return cents > 0 ? cents : null;
}

/** Cents as the form shows them for editing ("12.50" → "12,50" in German). */
export function centsToInput(cents: number, language: string, fractionDigits = 2): string {
  return new Intl.NumberFormat(language, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits, useGrouping: false }).format(cents / 100);
}

export function monthLabel(month: number, language: string, style: 'short' | 'long' = 'short'): string {
  return new Intl.DateTimeFormat(language, { month: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2000, month - 1, 1)));
}

export function formatSpentOn(day: string, language: string): string {
  return new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${day}T00:00:00Z`));
}

/** Change against a base in percent, rounded; null when there is nothing to compare with. */
export function percentChange(current: number, previous: number): number | null {
  if (previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

/** Months of the selected year already (partly) over — later ones have no actual spend yet. */
export function monthsElapsed(summary: Pick<BudgetSummary, 'year' | 'asOf'>): number {
  const asOfYear = Number(summary.asOf.slice(0, 4));
  if (summary.year < asOfYear) return 12;
  if (summary.year > asOfYear) return 0;
  return Number(summary.asOf.slice(5, 7));
}

export interface CumulativePoint {
  month: number;
  /** Running total of this year; null after the current month. */
  actual: number | null;
  /** Running total with the forecast; from the last complete month on (so the line joins the actual one), null outside the current year. */
  forecast: number | null;
  previous: number;
}

/** Running totals per month for the cumulative chart (euros, not cents, for the axis). */
export function cumulativeSeries(summary: BudgetSummary): CumulativePoint[] {
  const elapsed = monthsElapsed(summary);
  let actual = 0;
  let projected = 0;
  let previous = 0;
  return summary.months.map((m: MonthSpend, i) => {
    actual += m.totalCents;
    projected += m.projectedCents ?? m.totalCents;
    previous += summary.previousMonths[i]?.totalCents ?? 0;
    const month = i + 1;
    return {
      month,
      actual: month <= elapsed ? actual / 100 : null,
      forecast: summary.isCurrentYear && month >= elapsed - 1 ? projected / 100 : null,
      previous: previous / 100,
    };
  });
}

/** Rolling average over the last `window` values (fewer at the start); nulls stay null and are skipped. */
export function rollingAverage(values: readonly (number | null)[], window = 3): (number | null)[] {
  return values.map((v, i) => {
    if (v === null) return null;
    const recent = values.slice(Math.max(0, i - window + 1), i + 1).filter((x): x is number => x !== null);
    return Math.round((recent.reduce((a, b) => a + b, 0) / recent.length) * 100) / 100;
  });
}
